import { useTranslation } from "react-i18next";
import { 
  Phone, 
  Shield, 
  MapPin, 
  Bell, 
  Clock, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Globe,
  HeartPulse
} from "lucide-react";
import { Button } from "@/components/ui/button";

const DutyOfCareSection = () => {
  const { t } = useTranslation();

  const sosFeatures = [
    {
      icon: Phone,
      title: t('dutyOfCare.features.sos247.title'),
      description: t('dutyOfCare.features.sos247.description'),
    },
    {
      icon: MapPin,
      title: t('dutyOfCare.features.tracking.title'),
      description: t('dutyOfCare.features.tracking.description'),
    },
    {
      icon: Bell,
      title: t('dutyOfCare.features.alerts.title'),
      description: t('dutyOfCare.features.alerts.description'),
    },
    {
      icon: Shield,
      title: t('dutyOfCare.features.insurance.title'),
      description: t('dutyOfCare.features.insurance.description'),
    },
    {
      icon: HeartPulse,
      title: t('dutyOfCare.features.medical.title'),
      description: t('dutyOfCare.features.medical.description'),
    },
    {
      icon: Globe,
      title: t('dutyOfCare.features.evacuation.title'),
      description: t('dutyOfCare.features.evacuation.description'),
    },
  ];

  // Sample employee cards for the visual demo
  const employeeCards = [
    {
      name: "Sophie Martin",
      location: "Bangkok, Thailand",
      status: "safe",
      threatLevel: "low",
      daysRemaining: 12,
      avatar: "SM",
    },
    {
      name: "James Wilson",
      location: "São Paulo, Brazil",
      status: "safe",
      threatLevel: "medium",
      daysRemaining: 8,
      avatar: "JW",
    },
    {
      name: "Elena Rodriguez",
      location: "Dubai, UAE",
      status: "alert",
      threatLevel: "elevated",
      daysRemaining: 21,
      avatar: "ER",
    },
  ];

  const getThreatLevelStyles = (level: string) => {
    switch (level) {
      case "low":
        return { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" };
      case "medium":
        return { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" };
      case "elevated":
        return { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500" };
      case "high":
        return { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" };
      default:
        return { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-500" };
    }
  };

  const scrollToDemo = () => {
    const demoSection = document.getElementById('final-cta');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 sm:py-28 px-4 bg-muted/30">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="h-4 w-4" />
            {t('dutyOfCare.badge')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t('dutyOfCare.title')}
            <span className="text-primary block mt-2">{t('dutyOfCare.titleHighlight')}</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('dutyOfCare.subtitle')}
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Features Grid */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-foreground mb-8">
              {t('dutyOfCare.featuresTitle')}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {sosFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="p-5 bg-white rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="pt-6">
              <Button onClick={scrollToDemo} size="lg" className="rounded-full px-8">
                {t('dutyOfCare.cta')}
              </Button>
            </div>
          </div>

          {/* Right: Employee Cards Demo */}
          <div className="relative">
            <div className="bg-white rounded-2xl border border-border p-6 shadow-xl">
              {/* SOS Center Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      {t('dutyOfCare.sosCenter.title')}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {t('dutyOfCare.sosCenter.subtitle')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">24/7</span>
                </div>
              </div>

              {/* Live Status */}
              <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium text-green-700">
                  {t('dutyOfCare.sosCenter.liveStatus')}
                </span>
                <Users className="h-4 w-4 text-green-600 ml-auto" />
                <span className="text-sm text-green-700">3</span>
              </div>

              {/* Employee Cards */}
              <div className="space-y-3">
                {employeeCards.map((employee, index) => {
                  const threatStyles = getThreatLevelStyles(employee.threatLevel);
                  return (
                    <div
                      key={index}
                      className="p-4 bg-muted/30 rounded-xl border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary flex-shrink-0">
                          {employee.avatar}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-foreground truncate">
                              {employee.name}
                            </span>
                            {employee.status === "safe" ? (
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{employee.location}</span>
                          </div>
                        </div>

                        {/* Threat Level */}
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${threatStyles.bg} ${threatStyles.text} font-medium capitalize`}>
                            {employee.threatLevel}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {employee.daysRemaining}d {t('dutyOfCare.sosCenter.remaining')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Actions */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    <Bell className="h-4 w-4 mr-2" />
                    {t('dutyOfCare.sosCenter.sendAlert')}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <MapPin className="h-4 w-4 mr-2" />
                    {t('dutyOfCare.sosCenter.viewMap')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -z-10 -top-4 -right-4 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute -z-10 -bottom-4 -left-4 w-48 h-48 bg-orange-500/5 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DutyOfCareSection;
